import React, { useCallback, useState, useRef, useMemo, useEffect } from "react"
import type { PropsWithChildren } from "react"
import { Platform, ScrollView } from "react-native"
import type {
  NativeSyntheticEvent,
  TextInputKeyPressEventData,
} from "react-native"
import { cn } from "../../styles/utils"
import { Box } from "../Box"
import { Pressable } from "../Pressable"
import { TextInput } from "./TextInput"

export interface SearchFilterFieldItem {
  id: string
  data: any
}

export interface SearchFilterFieldProps
  extends PropsWithChildren<{
    items: SearchFilterFieldItem[]
    renderItem: (
      item: SearchFilterFieldItem,
      index: number,
      isSelected: boolean,
    ) => React.ReactNode
    onItemSelect: (item: SearchFilterFieldItem, index: number) => void
    placeholder?: string
    searchValue?: string
    onSearchChange?: (value: string) => void
    onEnterPress?: () => void
    className?: string
    suggestionsBoxClassName?: string
    name: string
    hideError?: boolean
    showSearchIcon?: boolean
    itemHeight?: number
  }> {}

export const SearchFilterField: React.FC<SearchFilterFieldProps> = ({
  items,
  renderItem,
  onItemSelect,
  placeholder = "Search...",
  searchValue = "",
  onSearchChange,
  onEnterPress,
  className,
  suggestionsBoxClassName,
  name,
  hideError = true,
  showSearchIcon = true,
  children,
  itemHeight = 56,
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [selectedSuggestionIndex, setSelectedSuggestionIndex] = useState(-1)
  const [scrollPosition, setScrollPosition] = useState(0)
  const [visibleHeight, setVisibleHeight] = useState(0)
  const inputRef = useRef<any>(null)
  const scrollViewRef = useRef<ScrollView>(null)

  // Auto-scroll to keep selected item visible
  useEffect(() => {
    if (
      selectedSuggestionIndex >= 0 &&
      scrollViewRef.current &&
      visibleHeight > 0
    ) {
      const itemTop = selectedSuggestionIndex * itemHeight
      const itemBottom = itemTop + itemHeight

      // Check if item is already visible
      const viewportTop = scrollPosition
      const viewportBottom = scrollPosition + visibleHeight

      // Only scroll if item is not fully visible
      if (itemTop < viewportTop) {
        // Item is above visible area, scroll up
        scrollViewRef.current.scrollTo({
          y: itemTop,
          animated: true,
        })
      } else if (itemBottom > viewportBottom) {
        // Item is below visible area, scroll down
        scrollViewRef.current.scrollTo({
          y: itemBottom - visibleHeight,
          animated: true,
        })
      }
    }
  }, [selectedSuggestionIndex, itemHeight, scrollPosition, visibleHeight])

  const handleInputSubmit = useCallback(() => {
    if (
      selectedSuggestionIndex >= 0 &&
      selectedSuggestionIndex < items.length
    ) {
      onItemSelect(items[selectedSuggestionIndex], selectedSuggestionIndex)
      setShowSuggestions(false)
      setSelectedSuggestionIndex(-1)
    } else if (onEnterPress) {
      onEnterPress()
    }
  }, [selectedSuggestionIndex, items, onItemSelect, onEnterPress])

  const handleInputKeyPress = useCallback(
    (event: NativeSyntheticEvent<TextInputKeyPressEventData>) => {
      if (Platform.OS !== "web") return

      const { key } = event.nativeEvent

      if (key === "ArrowDown") {
        event.preventDefault()
        if (showSuggestions && items.length > 0) {
          const newIndex =
            selectedSuggestionIndex < items.length - 1
              ? selectedSuggestionIndex + 1
              : selectedSuggestionIndex // Stay at last item, don't wrap
          setSelectedSuggestionIndex(newIndex)
        } else if (items.length > 0) {
          setShowSuggestions(true)
          setSelectedSuggestionIndex(0)
        }
      } else if (key === "ArrowUp") {
        event.preventDefault()
        if (showSuggestions && selectedSuggestionIndex > 0) {
          const newIndex = selectedSuggestionIndex - 1
          setSelectedSuggestionIndex(newIndex)
        } else if (showSuggestions && selectedSuggestionIndex === 0) {
          setSelectedSuggestionIndex(-1)
          inputRef.current?.focus()
        }
      } else if (key === "Escape") {
        setShowSuggestions(false)
        setSelectedSuggestionIndex(-1)
      }
    },
    [showSuggestions, selectedSuggestionIndex, items.length],
  )

  const handleInputChange = useCallback(
    (value: string) => {
      if (onSearchChange) {
        onSearchChange(value)
      }
      setSelectedSuggestionIndex(-1)
      if (value.trim() || items.length > 0) {
        setShowSuggestions(true)
      }
    },
    [onSearchChange, items.length],
  )

  const handleFocus = useCallback(() => {
    if (items.length > 0) {
      setShowSuggestions(true)
    }
  }, [items.length])

  const handleBlur = useCallback(() => {
    setTimeout(() => setShowSuggestions(false), 150)
  }, [])

  const handleScrollViewScroll = useCallback((event: any) => {
    setScrollPosition(event.nativeEvent.contentOffset.y)
  }, [])

  const handleScrollViewLayout = useCallback((event: any) => {
    setVisibleHeight(event.nativeEvent.layout.height)
  }, [])

  const handleItemKeyPress = useCallback(
    (
      event: NativeSyntheticEvent<TextInputKeyPressEventData>,
      item: SearchFilterFieldItem,
      index: number,
    ) => {
      if (Platform.OS !== "web") return

      const { key } = event.nativeEvent

      if (key === "Enter") {
        event.preventDefault()
        onItemSelect(item, index)
        setShowSuggestions(false)
        setSelectedSuggestionIndex(-1)
      } else if (key === "ArrowDown") {
        event.preventDefault()
        const newIndex = index < items.length - 1 ? index + 1 : index // Stay at last item, don't wrap
        setSelectedSuggestionIndex(newIndex)
      } else if (key === "ArrowUp") {
        event.preventDefault()
        if (index > 0) {
          setSelectedSuggestionIndex(index - 1)
        } else {
          setSelectedSuggestionIndex(-1)
          inputRef.current?.focus()
        }
      } else if (key === "Escape") {
        event.preventDefault()
        setSelectedSuggestionIndex(-1)
        setShowSuggestions(false)
        inputRef.current?.focus()
      }
    },
    [items.length, onItemSelect],
  )

  const handleItemSelect = useCallback(
    (item: SearchFilterFieldItem, index: number) => {
      onItemSelect(item, index)
      setShowSuggestions(false)
      setSelectedSuggestionIndex(-1)
    },
    [onItemSelect],
  )

  const memoizedItems = useMemo(
    () =>
      items.map((item, index) => {
        const isSelected = selectedSuggestionIndex === index

        const handleKeyPress = (
          event: NativeSyntheticEvent<TextInputKeyPressEventData>,
        ) => {
          handleItemKeyPress(event, item, index)
        }

        const handlePress = () => {
          handleItemSelect(item, index)
        }

        return (
          <Pressable
            key={item.id}
            onPress={handlePress}
            onKeyPress={Platform.OS === "web" ? handleKeyPress : undefined}
            className={cn(
              "border-b border-outline-100 hover:bg-primary-50",
              isSelected && "bg-primary-50",
            )}
            style={{
              ...(Platform.OS === "web" && { tabIndex: 0 }),
            }}
          >
            {renderItem(item, index, isSelected)}
          </Pressable>
        )
      }),
    [
      items,
      selectedSuggestionIndex,
      renderItem,
      handleItemKeyPress,
      handleItemSelect,
    ],
  )

  return (
    <Box className={cn("relative", className)}>
      {/* Input field with search icon */}
      <Box className="relative">
        <TextInput
          textInputClassName="w-full"
          ref={inputRef}
          value={searchValue}
          handleChange={handleInputChange}
          onEnterPress={handleInputSubmit}
          onFocus={handleFocus}
          onBlur={handleBlur}
          onKeyPress={Platform.OS === "web" ? handleInputKeyPress : undefined}
          placeholder={placeholder}
          name={name}
          hideError={hideError}
          rightIcon={showSearchIcon ? "search" : undefined}
        />
      </Box>

      {/* Container for children content and suggestions */}
      <Box className="relative overflow">
        {/* Children content */}
        {children}

        {/* Suggestions list */}
        {showSuggestions && items.length > 0 && (
          <Box className="absolute top-0 left-0 right-0 z-10">
            <ScrollView
              ref={scrollViewRef}
              className={cn(
                "border border-outline-200 rounded-md bg-background-0 shadow-lg max-h-32",
                suggestionsBoxClassName,
              )}
              nestedScrollEnabled
              scrollEventThrottle={50}
              onScroll={handleScrollViewScroll}
              onLayout={handleScrollViewLayout}
            >
              {memoizedItems}
            </ScrollView>
          </Box>
        )}
      </Box>
    </Box>
  )
}
